import React, { FC } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { analyticsService } from '../../analytics/AnalyticsService';
import { useScale } from '../../hooks/useScale';
import { useColors } from '../../hooks/useColors';
import { TEXT_SIZE_OPTIONS } from '../../common/constants';
import { Scale, Theme as ThemeOptions } from '../../common/types';
import Icon from '../../component/Icon/Icon';
import SectionHeading from './SectionHeading';
import SettingsScreenStyles from './Settings.styles';
import AppearanceSettingsStyles from './AppearanceSettings.styles';
import ModifierStylingSettings from './ModifierStylingSettings';

const PREVIEW_ORDER_META = '#1042 · Table 6';
const PREVIEW_TIME = '2:14';
const PREVIEW_ITEM = '2 × Flat White';
const PREVIEW_MODIFIERS = 'Oat milk · Extra hot';

const AppearanceSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const appearance = AppearanceSettingsStyles();
  const { translate } = useTranslation();
  const { setScale, currentScale } = useScale();
  const { colors, activeTheme, changeTheme } = useColors();

  const isDarkMode = activeTheme === ThemeOptions.DARK;

  const onToggleDarkMode = () => {
    if (isDarkMode) {
      analyticsService.capture('Changed Theme to Light');
      changeTheme(ThemeOptions.LIGHT);
    } else {
      analyticsService.capture('Changed Theme to Dark');
      changeTheme(ThemeOptions.DARK);
    }
  };

  const onSelectSize = (value: Scale) => {
    setScale(value);
    analyticsService.capture(`Text Scale Changed to ${value}`);
  };

  return (
    <View style={styles.panel} ph-label="settings-appearance">
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-textSize"
          title={translate('kitchenDisplay.textSize')}
        />
        <View style={appearance.card}>
          <View style={appearance.tilesRow} accessibilityRole="radiogroup">
            {TEXT_SIZE_OPTIONS.map(size => {
              const selected = size.value === currentScale;
              const glyphStyle = {
                [Scale.SMALL]: appearance.tileGlyphSMALL,
                [Scale.MEDIUM]: appearance.tileGlyphMEDIUM,
                [Scale.LARGE]: appearance.tileGlyphLARGE,
              }[size.value];
              return (
                <TouchableOpacity
                  key={size.value}
                  testID={`textSize-${size.label.toLowerCase()}`}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selected }}
                  style={[appearance.tile, selected && appearance.tileSelected]}
                  onPress={() => onSelectSize(size.value)}
                >
                  <Text style={[appearance.tileGlyph, glyphStyle]}>Aa</Text>
                  <Text
                    style={[
                      appearance.tileLabel,
                      selected && appearance.tileLabelSelected,
                    ]}
                  >
                    {translate(`kitchenDisplay.${size.label}`)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={appearance.divider} />

          <Text style={appearance.previewLabel}>
            {translate('kitchenDisplay.textSizePreview')}
          </Text>
          <View style={appearance.previewDocket} testID="textSize-preview">
            <View style={appearance.previewHeaderRow}>
              <Text style={appearance.previewMeta}>{PREVIEW_ORDER_META}</Text>
              <Text style={appearance.previewMeta}>{PREVIEW_TIME}</Text>
            </View>
            <Text style={appearance.previewItemName}>{PREVIEW_ITEM}</Text>
            <Text style={appearance.previewModifiers}>{PREVIEW_MODIFIERS}</Text>
          </View>
        </View>
      </View>

      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-theme"
          title={translate('kitchenDisplay.theme')}
        />
        <TouchableOpacity
          testID="toggle-darkMode"
          accessibilityRole="switch"
          accessibilityState={{ checked: isDarkMode }}
          style={appearance.themeCard}
          onPress={onToggleDarkMode}
        >
          <View style={appearance.themeTextGroup}>
            <Text style={appearance.themeTitle}>
              {translate('kitchenDisplay.darkMode')}
            </Text>
            <Text style={appearance.themeSubtitle}>
              {translate('kitchenDisplay.darkModeSubtitle')}
            </Text>
          </View>
          <Icon
            size={30}
            name={`toggle-${isDarkMode ? 'on' : 'off'}`}
            color={isDarkMode ? colors.green : colors.grey5}
          />
        </TouchableOpacity>
      </View>

      <ModifierStylingSettings />
    </View>
  );
};

export default AppearanceSettings;
